import { useCallback } from 'react';

import { useHttpClient } from '@work4all/data';

export const SERVICE_ACCESS_API_BASE_URL =
  'https://serviceaccess.work4all.work4allcloud.de/api';

const DEFAULT_HEADERS = {
  'content-type': 'application/json',
};

export enum RequestStatus {
  OPEN = 0,
  GRANTED = 1,
  DENIED = 2,
  EXPIRED = 3,
}

export type ValidateExternalRequestStatus =
  | 'OK'
  | 'INVALID'
  | 'NOT_FOUND'
  | 'REQUEST_ALREADY_HANDLED';

export const validateExternalRequestStatus = {
  200: 'OK',
  403: 'INVALID',
  404: 'NOT_FOUND',
  410: 'REQUEST_ALREADY_HANDLED',
};

export type InternalRequest = {
  id: string;
  ticketId: string;
  status: RequestStatus;
  supporterCode: number;
  customerId: number;
  denyReason: string;
  backend: string;
};

export type ExternalRequest = {
  id: string;
  supporterName: string;
  status: RequestStatus;
  customerId: number;
};

export const useServiceAccessApi = () => {
  const httpClient = useHttpClient();

  const headers = DEFAULT_HEADERS;

  const createInternalRequest = useCallback(
    async (
      ticketId: string,
      customerNumber?: number,
      contactId?: number
    ): Promise<string> => {
      try {
        const body = {
          ticketId: ticketId,
          contactCode: contactId,
          customerId: customerNumber?.toString(),
        };
        const response = await httpClient.post({
          url: `${SERVICE_ACCESS_API_BASE_URL}/internal/request`,
          body,
          headers,
        });
        return response.data;
      } catch (error) {
        console.error(error);
      }
      return null;
    },
    [headers, httpClient]
  );

  const deleteInternalRequest = useCallback(
    async (requestId: string): Promise<void> => {
      try {
        await httpClient.delete({
          url: `${SERVICE_ACCESS_API_BASE_URL}/internal/request?requestId=${requestId}`,
          headers,
        });
      } catch (error) {
        console.error(error);
      }
    },
    [headers, httpClient]
  );

  const getInternalRequestList = useCallback(
    async (status: RequestStatus[] = []): Promise<InternalRequest[]> => {
      const statusString = status.map((s) => `status=${s}`).join('&');
      try {
        const response = await httpClient.get({
          url: `${SERVICE_ACCESS_API_BASE_URL}/internal/request/list?${statusString}`,
          headers,
        });
        return response.data;
      } catch (error) {
        console.error(error);
      }
      return [];
    },
    [headers, httpClient]
  );

  const getMyInternalRequestList = useCallback(
    async (status: RequestStatus[] = []): Promise<InternalRequest[]> => {
      const statusString = status.map((s) => `status=${s}`).join('&');
      try {
        const response = await httpClient.get({
          url: `${SERVICE_ACCESS_API_BASE_URL}/internal/request/list/mine?${statusString}`,
          headers,
        });
        return response.data;
      } catch (error) {
        console.error(error);
      }
      return [];
    },
    [headers, httpClient]
  );

  const getLoginUrl = useCallback(
    async (id: string): Promise<string> => {
      try {
        const response = await httpClient.get({
          url: `${SERVICE_ACCESS_API_BASE_URL}/Internal/Request/${id}/GetLoginUrl`,
          responseType: 'text',
        });
        return response.data;
      } catch (error) {
        console.error(error);
      }
      return null;
    },
    [httpClient]
  );

  const validateExternalRequest = useCallback(
    async (id: string, baseUrl: string): Promise<boolean> => {
      const res = await httpClient
        .get({
          url: `${baseUrl}/Auth/ValidateServiceAccessRequest?requestId=${id}`,
          headers,
        })
        .then(() => {
          return true;
        })
        .catch((err) => {
          console.error(err);
          return false;
        });

      return res;
    },
    [headers, httpClient]
  );

  const grantExternalRequest = useCallback(
    async (id: string, hoursValid: number, baseUrl: string): Promise<void> => {
      try {
        const response = await httpClient.get({
          url: `${baseUrl}/Auth/GrantServiceAccessRequest?requestId=${id}&hoursValid=${hoursValid}`,
          headers,
        });
        return response.data;
      } catch (error) {
        console.error(error);
      }
      return null;
    },
    [headers, httpClient]
  );

  const denyExternalRequest = useCallback(
    async (id: string, baseUrl: string, denyReason = ''): Promise<void> => {
      try {
        const body = {
          denyReason,
        };
        const response = await httpClient.post({
          url: `${baseUrl}/Auth/DenyServiceAccessRequest?requestId=${id}`,
          headers,
          body,
        });
        return response.data;
      } catch (error) {
        console.error(error);
      }
      return null;
    },
    [headers, httpClient]
  );

  return {
    createInternalRequest,
    deleteInternalRequest,
    getInternalRequestList,
    getMyInternalRequestList,
    validateExternalRequest,
    grantExternalRequest,
    denyExternalRequest,
    getLoginUrl,
  };
};
